import React, { ReactNode } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { ConfirmPopup } from 'primereact/confirmpopup';
import Breadcrumb from '../../components/Breadcrumb';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { ComponentState, leaveComponent } from '../../redux/actions/ComponentActions';
import { changePageFilter, clearPage } from '../../redux/actions/PageActions';
import CustomEditorItemComponent from '../../components/custom/CustomEditorItemComponent';
import ComponentName from '../../constant/ComponentName';
import { dispatch, RootState, store } from '../../redux/store';
import CraTypeRadioMenu from '../../components/cra/policy/CraTypeRadioMenu';
import { CraType } from '../../constant/Constants';
import CustomLoading from '../../components/custom/CustomLoading';
import EmptyListComponent from 'components/custom/empty-components/EmptyListComponent';
import CustomTable, { CustomColumnProps } from 'components/custom/CustomTable';
import CustomPaginator, { CustomPaginatorProps } from '../../components/custom/CustomPaginator';
import LanguageUtil from '../../util/LanguageUtil';
import CustomTableContentWrapper from '../../components/custom/CustomTableContentWrapper';
import ItemToolsMenu from '../../components/ItemToolsMenu';
import _ from 'lodash';
import Filter = query.Filter;
import { query } from '@trlogic/client-js';
import { CustomFilterFields } from '../../domain/ui/FilterDomains';
import FilterUtil from '../../util/FilterUtil';
import DefaultFilterUtil from '../../util/DefaultFilterUtil';
import ValueFactory from '../../util/ValueFactory';
import CollectionNames from '../../constant/CollectionNames';
import { CraAssessmentResult, CraDetailedScore } from '../../domain/cra/CraDomains';
import TransactionResultModalComponent from '../../components/investigation/case/widget/TransactionResultModalComponent';
import { DefaultFilter, UserSystemAttributes } from '../../domain/rbac/RbacDomains';
import FolderService from '../../service/FolderService';
import CraService from '../../service/CraService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import JobService from '../../service/JobService';
import { CraResultExportJob } from '../../domain/job/JobDomain';
import MessageUtil from '../../util/MessageUtil';
import { initialPagePath } from '../../redux/reducers/PageReducer';
import moment from 'moment';
import and = query.and;
import eq = query.eq;

class CraAssessmentContainer extends React.Component<Props, State> {
  public state: State = new State();

  private readonly craService: CraService = ServiceContainer.getInstance().get(ServiceNames.CRA);
  private readonly jobService: JobService = ServiceContainer.getInstance().get(ServiceNames.JOB);

  constructor(props: Props) {
    super(props);
  }

  public render(): ReactNode {
    const { loading, assessmentResults, selectedType, detailedScoresVisible, selectedDetailedScores, rows, currentPage, totalRecord } =
      this.state;
    const {
      component: { selectedComponentIds },
      filter,
    } = this.props;

    return (
      <div className={'flex flex-col h-full w-full gap-y-2 overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        {detailedScoresVisible && selectedDetailedScores && (
          <TransactionResultModalComponent
            result={selectedDetailedScores}
            visible={detailedScoresVisible}
            onClose={this.closeDetailedScores}
          />
        )}
        <div className={'shrink-0'}>
          <CustomEditorItemComponent
            name={LanguageUtil.get({ id: 'pages.cra.title', defaultMessage: 'Assessment' })}
            description=''
            status='ACTIVE'
            component={ComponentName.ASSESSMENT}
            isUpdate={false}
            icon={'sidebar-assessment'}
            onChangeName={() => {}}
            onChangeDescription={() => {}}
            disableRemove={true}
            disableNameChange={true}
            disableDescriptionChange={true}
          />
        </div>
        <div className={'flex flex-row justify-between items-center px-5 shrink-0'}>
          <CraTypeRadioMenu onChangeType={this.onChangeType} value={selectedType} />
        </div>
        <div className={'flex min-h-[24px] px-5 justify-start bg-white items-center w-full shrink-0'}>
          <ItemToolsMenu
            views={true}
            id='item-tools-assessment'
            itemsIsSelected={selectedComponentIds.length > 0}
            component={ComponentName.ASSESSMENT}
            componentId={selectedType}
            onChangeFilter={this.onChangeFilter}
            importVisible={false}
            componentLabel={LanguageUtil.get({ id: 'common.label.assessment', defaultMessage: 'Assessment' })}
            totalCount={totalRecord}
            filter={FilterUtil.parse(filter.filter)}
            exportVisibleAlways
            exportFunc={this.onExport}
            sessionCheck
            countVisible={false}
          />
        </div>
        <CustomPaginator
          rows={rows}
          currentPage={currentPage}
          currentRecords={assessmentResults.length}
          rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
          totalRecords={totalRecord}
          loading={loading}
          onPageChange={this.onPageChange}
        />
        <div className={'flex h-full w-full px-5'}>
          {loading ? (
            <CustomLoading />
          ) : assessmentResults.length === 0 ? (
            <EmptyListComponent />
          ) : (
            <div
              className={
                'text-md text-f-black-2 overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full rounded-lg'
              }
            >
              <CustomTable columns={this.getColumns()} value={assessmentResults} />
            </div>
          )}
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const {
      router: { location, params },
    } = this.props;

    const assessorFromUrl = params.id as CraType | undefined;
    const selectedType = assessorFromUrl || this.state.selectedType;
    const page = store.getState().page;
    const initial = initialPagePath[ComponentName.ASSESSMENT];

    if (page.pagePath.length == 0 || page.pagePath[0].title != initial?.title) {
      await FolderService.calculate('WITHOUT_FOLDER', ComponentName.ASSESSMENT, location.pathname, store.getState().page);
    }
    if (selectedType && selectedType !== this.state.selectedType) {
      this.setState({ selectedType }, () => {
        this.prepareFilterFields();
      });
    } else {
      this.prepareFilterFields();
    }
  }

  private loadAssessmentResults = async (filterOverride?: Filter) => {
    const { filter } = this.props;
    const { selectedType, rows, currentPage } = this.state;
    const requestType = selectedType;
    const requestPage = currentPage;
    this.setState({ loading: true });
    try {
      const parsedFilter = filterOverride ?? FilterUtil.parse(filter.filter);
      const assessmentQuery = query.QueryBuilder.select()
        .from(CollectionNames.CRA_RESULT)
        .where(and(parsedFilter, eq('assessor', selectedType)))
        .orderBy({ field: 'createdAt', operator: 'DESC' })
        .limit(rows)
        .skip((currentPage - 1) * rows)
        .build();

      const result: CraAssessmentResult[] = await this.craService.getAssessmentResults(selectedType, assessmentQuery);
      const totalRecord: number | undefined = await this.getTotalRecord(parsedFilter);
      this.setState((state) => {
        if (state.selectedType !== requestType || state.currentPage !== requestPage) {
          return { loading: false };
        }
        return {
          loading: false,
          assessmentResults: result,
          totalRecord: totalRecord ?? state.totalRecord,
        };
      });
    } catch (e) {
      this.setState((state) => {
        if (state.selectedType !== requestType || state.currentPage !== requestPage) {
          return state;
        }
        return { loading: false };
      });
    }
  };

  private getTotalRecord = async (parsedFilter?: Filter): Promise<number | undefined> => {
    const { filter } = this.props;
    const { selectedType } = this.state;
    const countFilter = parsedFilter ?? FilterUtil.parse(filter.filter);
    const countQuery = query.QueryBuilder.select().from(CollectionNames.CRA_RESULT).where(countFilter).build();
    return await this.craService.countAssessmentResults(selectedType, countQuery);
  };

  private onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page, loading: true }, () => {
      void this.loadAssessmentResults();
    });
  };

  private onChangeFilter = async (_filter: Filter) => {
    this.setState({ currentPage: 1 }, () => {
      void this.loadAssessmentResults(_filter);
    });
  };

  public async componentWillUnmount() {
    const { leaveComponent, clearPage } = this.props;
    leaveComponent();
    clearPage();
  }

  private onChangeType = async (assessor: CraType) => {
    const {
      router: { navigate, location },
    } = this.props;
    navigate(`/assessment/${assessor}`);
    location.pathname = `/assessment/${assessor}`;
    this.setState({ selectedType: assessor, currentPage: 1 }, async () => {
      await this.prepareFilterFields();
    });
  };

  private getScoreItem = (score: number) => {
    if (score == 0) {
      return 'bg-[#DBF8E5] text-[#3F9E2C]';
    } else if (score > 0 && score < 100) {
      return 'bg-[#FCF1BB] text-[#C57500]';
    } else if (score >= 100) {
      return 'bg-[#FFF1F3] text-[#D2332F]';
    }
  };

  private prepareFilterFields = async () => {
    const { attributes, filter, user } = this.props;
    const fields: CustomFilterFields[] = await FilterUtil.getFields(ComponentName.ASSESSMENT);
    const { selectedType } = this.state;

    const defaultFilter: DefaultFilter | undefined = _.cloneDeep(
      DefaultFilterUtil.find(attributes?.filters, {
        component: ComponentName.ASSESSMENT,
        id: selectedType,
        riskType: user!.activeRiskType,
      }),
    );

    const newFilter = defaultFilter?.filter.filter || ValueFactory.initializeFilter();

    dispatch(
      changePageFilter({
        filter: newFilter,
        items: fields,
      }),
    );
    await this.loadAssessmentResults(FilterUtil.parse(newFilter));
  };

  private showDetailedScores = async (id: string): Promise<void> => {
    const { selectedType } = this.state;
    const result = await this.craService.getAssessmentResultById(selectedType, id);
    if (result === undefined) {
      return;
    }
    const detailedScores = result.detailedScores;
    if (!Array.isArray(detailedScores) || detailedScores.length === 0) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.assessment', defaultMessage: 'CRA Assessment' }),
        detail: LanguageUtil.get({ id: 'pages.cra.table.noDetailedScores', defaultMessage: 'Detailed scores not found' }),
      });
      return;
    }
    this.setState({ selectedDetailedScores: detailedScores, detailedScoresVisible: true });
  };

  private closeDetailedScores = (): void => {
    this.setState({ selectedDetailedScores: undefined, detailedScoresVisible: false });
  };

  private onExport = async () => {
    const { filter } = this.props;
    const { selectedType } = this.state;
    try {
      const exportQuery = query.QueryBuilder.select().from(CollectionNames.CRA_RESULT).where(FilterUtil.parse(filter.filter)).build();
      const job: CraResultExportJob = {
        type: 'CRA_RESULT_EXPORT',
        timestamp: moment().valueOf(),
        status: 'PENDING',
        query: exportQuery,
        assessor: selectedType,
        extension: 'csv',
        path: '',
      };
      await this.jobService.schedule(job);
      MessageUtil.success({
        summary: 'Export',
        detail: LanguageUtil.get({ id: 'pages.cra.exportJobScheduled', defaultMessage: 'Export job scheduled.' }),
      });
    } catch (e) {
      console.error(e);
    }
  };

  private getColumns = (): CustomColumnProps[] => {
    return [
      {
        field: 'id',
        header: LanguageUtil.get({ id: 'pages.cra.table.id', defaultMessage: 'ID' }),
        className: 'w-1/5',
        copyable: true,
        onCopy: (data: CraAssessmentResult) => data.id,
        body: (data: CraAssessmentResult) => <CustomTableContentWrapper text={data.id || 'N/A'} clipSize={30} />,
      },
      {
        field: 'score',
        header: LanguageUtil.get({ id: 'pages.cra.table.score', defaultMessage: 'Score' }),
        className: 'w-1/5',
        body: (data: CraAssessmentResult) => {
          const score = data.score || 0;
          return (
            <div
              className={`flex flex-col justify-center items-center w-[40px] text-sm 2xl:text-md h-[24px] font-medium rounded-full ${this.getScoreItem(score)}`}
            >
              {Number.isInteger(score) ? score : score.toFixed(2)}
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.cra.table.createdAt', defaultMessage: 'Created At' }),
        className: 'w-1/5',
        body: (data: CraAssessmentResult) => {
          return data.createdAt ? new Date(data.createdAt).toLocaleDateString() : 'N/A';
        },
      },
      {
        field: 'lastUpdatedAt',
        header: LanguageUtil.get({ id: 'pages.cra.table.updatedAt', defaultMessage: 'Last Updated' }),
        className: 'w-1/5',
        body: (data: CraAssessmentResult) => {
          return data.lastUpdatedAt ? new Date(data.lastUpdatedAt).toLocaleDateString() : 'N/A';
        },
      },
      {
        field: 'detailedScores',
        header: LanguageUtil.get({ id: 'pages.cra.table.detailedScores', defaultMessage: 'Detailed Scores' }),
        className: 'w-1/5',
        sortable: false,
        alignHeader: 'center',
        body: (data: CraAssessmentResult) => {
          return (
            <div className={'flex flex-col justify-center items-center min-w-[86px] max-w-[150px] text-xs 2xl:text-md'}>
              <button onClick={() => this.showDetailedScores(data.id)} className={'text-[#458EF7]'}>
                {LanguageUtil.get({ id: 'pages.cra.table.showDetails', defaultMessage: 'Show Details' })}
              </button>
            </div>
          );
        },
      },
    ];
  };
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  selectedType: CraType = 'Individual';
  assessmentResults: CraAssessmentResult[] = [];
  loading: boolean = false;
  rows: number = 10;
  currentPage: number = 1;
  totalRecord: number = 0;
  detailedScoresVisible: boolean = false;
  selectedDetailedScores: CraDetailedScore[] | undefined;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component as ComponentState,
  filter: state.page.filter,
  attributes: state.auth.attributes as UserSystemAttributes,
  user: state.auth.user,
});

const mapDispatchToProps = {
  leaveComponent,
  clearPage,
  changePageFilter,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(CraAssessmentContainer));
